import { formatDate, formatPercentage } from '@if/core/utils'
import {
  Box,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  LinearProgress,
  Tab,
  TextField,
} from '@mui/material'
import { DataGrid, FooterPropsOverrides, GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { DataGridFooter } from '@src/components'
import { useSelectedCompany } from '@src/contexts/SelectedCompanyProvider'
import { useSearchPaymentPlans } from '@src/data/api/merchants-api/merchants-api'
import { Merchant } from '@src/data/types/Merchant'
import { PaymentPlanDto } from '@src/data/types/PaymentPlanDto'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
type PaymentPlanWithMerchants = PaymentPlanDto & { merchants?: Merchant[] }

const Promotions = () => {
  const { t } = useTranslation()
  const [pagination, setPagination] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 15,
  })

  const selectedCompany = useSelectedCompany()

  const columns = useMemo<GridColDef<PaymentPlanDto>[]>(() => [
    {
      field: 'id',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'promoCode',
      headerName: t('merchant.promo.promoCode'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const promoCodeStr = String(params.value)
        const id = params.row.id
        return (
          <Link
            to={`/promotions/${id}/merchants`}
            style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}
            title={promoCodeStr}
          >
            {promoCodeStr}
          </Link>
        )
      },
    },
    {
      field: 'activeFrom',
      headerName: t('merchant.promo.activeFrom'),
      valueFormatter: formatDate,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    },
    {
      field: 'expiresOn',
      headerName: t('browsePromotions.expiredOn'),
      valueFormatter: formatDate,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    },
    {
      field: 'reducedInterestRate',
      headerName: t('merchant.promo.reducedInterestRate'),
      valueFormatter: formatPercentage,
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    },
    {
      field: 'reducedRateDurationInMonths',
      headerName: t('merchant.promo.reducedRateDuration'),
      headerAlign: 'center',
      align: 'center',
      flex: 1,
    },
    {
      field: 'interestRate',
      headerName: t('merchant.promo.interestRate'),
      valueFormatter: formatPercentage,
      headerAlign: 'center',
      align: 'center',
      width: 120,
    },
    {
      field: 'loanTerm',
      headerName: t('merchant.promo.loanTerm'),
      headerAlign: 'center',
      align: 'center',
      width: 100,
    },
  ], [t])

  const [hasMerchantsOnlyFilter, setHasMerchantsOnlyFilter] = useState(true)
  const [activePromosOnlyFilter, setActivePromosOnlyFilter] = useState(true)

  const [paymentPlansList, isFetchingPaymentPlansList] = useSearchPaymentPlans({
    financingCompanyId: selectedCompany,
    isActive: activePromosOnlyFilter || undefined
  }) as [PaymentPlanWithMerchants[], boolean]

  const [promoCodeFilter, setPromoCodeFilter] = useState('')

  const filteredPaymentPlansList = useMemo(() => {
    let plans = paymentPlansList

    if (promoCodeFilter)
      plans = plans.filter((plan) => plan.promoCode.toLowerCase().includes(promoCodeFilter.toLowerCase()))

    if (hasMerchantsOnlyFilter) {
      plans = plans.filter((plan) => plan.hasMerchant === true)
    }

    return plans
  }, [paymentPlansList, promoCodeFilter, hasMerchantsOnlyFilter])
  const isPreviewDisabled = pagination.page === 0
  const isNextDisabled = (pagination.page + 1) * pagination.pageSize >= paymentPlansList.length

  return (
    <Box>
      <CardContent>
        <Tab label={t('common.promotions')} />

        <Grid container spacing={2} sx={{ alignItems: 'center', mb: 2 }}>
          <Grid>
            <TextField
              value={promoCodeFilter}
              onChange={(e) => setPromoCodeFilter(e.target.value)}
              label={t('common.search')}
              placeholder={t('merchant.promo.promoCode')}
            />
          </Grid>

          <Grid>
            <FormControlLabel
              control={
                <Checkbox
                  checked={activePromosOnlyFilter}
                  onChange={(e) => setActivePromosOnlyFilter(e.target.checked)}
                />
              }
              label={t('browsePromotions.filters.activePromosOnly')}
            />
          </Grid>

          <Grid>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasMerchantsOnlyFilter}
                  onChange={(e) => setHasMerchantsOnlyFilter(e.target.checked)}
                />
              }
              label={t('browsePromotions.filters.hasMerchantsOnly')}
            />
          </Grid>
        </Grid>

        {isFetchingPaymentPlansList ? (
          <LinearProgress></LinearProgress>
        ) : (
          <DataGrid
            initialState={{
                columns: {
                  columnVisibilityModel: {
                    id: false,
                  },
                },
              pagination: {
                paginationModel: {
                  pageSize: 100,
                },
              },
            }}
            paginationModel={pagination}
            onPaginationModelChange={(model: GridPaginationModel) => setPagination(model)}
            pageSizeOptions={[15, 25, 50, 100]}
            rows={filteredPaymentPlansList}
            columns={columns}
            disableRowSelectionOnClick
            slots={{
              footer: DataGridFooter,
            }}
            slotProps={{
              footer: {
                disabledPreview: isPreviewDisabled,
                disabledNext: isNextDisabled,
                pagination,
                setPagination,
              } as FooterPropsOverrides,
            }}
          />
        )}
      </CardContent>
    </Box>
  )
}
export default Promotions
